import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  ShiftNeed,
  PagedResultDto,
  Contractor,
  ShiftSearchDto,
  ShiftRequestDto,
  ShiftRequestSearchDto,
  CounterOfferRequestDto,
  CounterOfferDto,
  NegotiationHistoryDto,
  ApproveRequestsResponseDto
} from '@umss/shared/data-access';
import { CreateShiftNeedDto, UpdateShiftNeedDto } from '../dtos';
import { HttpHelper } from '@umss/shared/utils';
import { FacilityContextService } from './facility-context.service';

@Injectable({ providedIn: 'root' })
export class ShiftNeedService {
  private readonly apiUrl = '/api/shift-needs';
  private readonly facilityContext = inject(FacilityContextService);
  private get currentFacilityId(): string {
    return this.facilityContext.facilityId();
  }

  constructor(private http: HttpClient) {}

  /** Get a single shift need by ID */
  getOneShiftNeed(id: string): Observable<ShiftNeed> {
    return this.http.get<ShiftNeed>(`${this.apiUrl}/${id}`);
  }

  /** Get all shift needs with search and filtering */
  getShiftNeeds(
    searchDto?: ShiftSearchDto
  ): Observable<PagedResultDto<ShiftNeed>> {
    const params: ShiftSearchDto = {
      ...(this.currentFacilityId ? { facilityId: this.currentFacilityId } : {}),
      ...searchDto
    };

    return this.http.get<PagedResultDto<ShiftNeed>>(this.apiUrl, {
      params: HttpHelper.toHttpParams(params)
    });
  }

  /** Create a new shift need */
  createShiftNeed(data: CreateShiftNeedDto): Observable<ShiftNeed> {
    return this.http.post<ShiftNeed>(this.apiUrl, data);
  }

  /** Update an existing shift need */
  updateShiftNeed(id: string, data: UpdateShiftNeedDto): Observable<ShiftNeed> {
    return this.http.put<ShiftNeed>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Delete only expired shifts needs
   * @param id : string - Facility shift uuid
   * @returns : Observable<void> - Suscription to the delete request
   */
  deleteShiftNeed(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /** Contractor requests to fill a shift need */
  requestShift(
    shiftNeedId: string,
    requestDto: { requestedHourlyRate?: number; message?: string }
  ): Observable<ShiftRequestDto> {
    return this.http.post<ShiftRequestDto>(
      `${this.apiUrl}/${shiftNeedId}/request`,
      requestDto
    );
  }

  findShiftNeedsToManageRequests(
    params?: ShiftRequestSearchDto
  ): Observable<PagedResultDto<ShiftRequestDto>> {
    const searchParams: ShiftRequestSearchDto = {
      ...(this.currentFacilityId ? { facilityId: this.currentFacilityId } : {}),
      ...params
    };

    return this.http.get<PagedResultDto<ShiftRequestDto>>(
      `${this.apiUrl}/requests`,
      { params: HttpHelper.toHttpParams(searchParams) }
    );
  }

  /** This endpoint allows the facility to approve a contractor's request for a shift need */
  approveRequests(body: {
    requestIds: string[];
    responseMessage?: string;
  }): Observable<ApproveRequestsResponseDto> {
    return this.http.post<ApproveRequestsResponseDto>(
      `${this.apiUrl}/requests/approve`,
      body
    );
  }

  /** This endpoint allows the facility to reject a contractor's request for a shift need */
  rejectRequest(
    requestId: string,
    body: { responseMessage?: string }
  ): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/requests/${requestId}/reject`,
      body
    );
  }

  /** Find matching contractors for a shift need. Returns contractors who CAN work the shift (based on availability, scope of practice, etc.), not contractors who have already made requests. */
  findMatchingContractors(
    shiftNeedId: string
  ): Observable<PagedResultDto<Contractor>> {
    return this.http.get<PagedResultDto<Contractor>>(
      `${this.apiUrl}/${shiftNeedId}/matching-contractors`
    );
  }

  /** Send urgent shift notifications */
  notifyUrgentShift(shiftNeedId: string): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/${shiftNeedId}/notify-urgent`,
      {}
    );
  }

  /**
   * Facility submits a counter-offer on an urgent shift request.
   * POST /api/shift-needs/requests/{requestId}/counter-offer
   */
  submitCounterOffer(
    requestId: string,
    body: CounterOfferRequestDto
  ): Observable<CounterOfferDto> {
    return this.http.post<CounterOfferDto>(
      `${this.apiUrl}/requests/${requestId}/counter-offer`,
      body
    );
  }

  /**
   * Fetch the full negotiation history for a shift request.
   * GET /api/shift-requests/{requestId}/negotiation-history
   */
  getNegotiationHistory(requestId: string): Observable<NegotiationHistoryDto> {
    return this.http.get<NegotiationHistoryDto>(
      `/api/shift-requests/${requestId}/negotiation-history`
    );
  }

  /**
   * Facility accepts the contractor's latest counter-offer.
   * Wraps approveRequests for negotiation context.
   */
  acceptLatestOffer(requestId: string): Observable<void> {
    return this.approveRequests({ requestIds: [requestId] }).pipe(
      map(() => undefined)
    );
  }
}
